import { useState, useEffect } from 'react';
import {
  Cloud,
  Plus,
  Trash2,
  RefreshCw,
  Pencil,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { OneDriveShare } from '../../types';
import {
  getOneDriveShares,
  grantOneDriveAccess,
  updateOneDriveAccess,
  revokeOneDriveAccess,
} from '../../services/api';

interface OneDriveSharingProps {
  clientId: string;
  userId: string;
  userDisplayName: string;
}

export default function OneDriveSharing({ clientId, userId, userDisplayName }: OneDriveSharingProps) {
  const [shares, setShares] = useState<OneDriveShare[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Add share form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newShareEmail, setNewShareEmail] = useState('');
  const [newAccessLevel, setNewAccessLevel] = useState<'read' | 'write' | 'owner'>('read');

  // Edit mode
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editAccessLevel, setEditAccessLevel] = useState<'read' | 'write'>('read');

  async function fetchShares() {
    setLoading(true);
    setError(null);

    try {
      const response = await getOneDriveShares(clientId, userId);
      if (response.success) {
        // Filter out inherited permissions for cleaner view
        const directShares = response.shares.filter((s) => !s.isInherited);
        setShares(directShares);
      } else {
        setError(response.error || 'Failed to load OneDrive shares');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load OneDrive shares');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchShares();
  }, [clientId, userId]);

  async function handleAddShare() {
    if (!newShareEmail || !newShareEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setActionLoading(true);

    try {
      const response = await grantOneDriveAccess(clientId, userId, {
        delegateEmail: newShareEmail,
        accessLevel: newAccessLevel,
      });

      if (response.success) {
        toast.success('OneDrive access granted');
        setShowAddForm(false);
        setNewShareEmail('');
        setNewAccessLevel('read');
        fetchShares();
      } else {
        toast.error(response.error || 'Failed to grant access');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to grant access');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleUpdateAccess(permissionId: string) {
    setActionLoading(true);

    try {
      const response = await updateOneDriveAccess(clientId, userId, permissionId, editAccessLevel);

      if (response.success) {
        toast.success('Access level updated');
        setEditingId(null);
        fetchShares();
      } else {
        toast.error(response.error || 'Failed to update access');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update access');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleRevokeAccess(share: OneDriveShare) {
    if (!confirm(`Revoke OneDrive access for ${share.userName || share.userEmail}?`)) return;

    setActionLoading(true);

    try {
      const response = await revokeOneDriveAccess(clientId, userId, share.id);

      if (response.success) {
        toast.success('Access revoked');
        fetchShares();
      } else {
        toast.error(response.error || 'Failed to revoke access');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to revoke access');
    } finally {
      setActionLoading(false);
    }
  }

  function getAccessLevelBadge(level: string) {
    switch (level) {
      case 'owner':
        return (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
            Owner
          </span>
        );
      case 'write':
        return (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
            Edit
          </span>
        );
      default:
        return (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-muted text-muted-foreground">
            View
          </span>
        );
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-foreground flex items-center">
          <Cloud className="h-4 w-4 mr-2 text-muted-foreground" />
          OneDrive Sharing for {userDisplayName}
        </h4>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchShares}
            disabled={loading}
            className="text-muted-foreground hover:text-foreground"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-800"
          >
            <Plus className="h-4 w-4 mr-1" />
            Share OneDrive
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-3">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Add Share Form */}
      {showAddForm && (
        <div className="bg-muted rounded-lg p-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Share with (email)
            </label>
            <input
              type="email"
              value={newShareEmail}
              onChange={(e) => setNewShareEmail(e.target.value)}
              placeholder="user@company.com"
              className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 text-sm"
              disabled={actionLoading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Access Level
            </label>
            <select
              value={newAccessLevel}
              onChange={(e) => setNewAccessLevel(e.target.value as 'read' | 'write' | 'owner')}
              className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 text-sm"
              disabled={actionLoading}
            >
              <option value="read">View only</option>
              <option value="write">Edit</option>
              <option value="owner">Owner (full control)</option>
            </select>
          </div>

          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowAddForm(false)}
              disabled={actionLoading}
              className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
            <button
              onClick={handleAddShare}
              disabled={actionLoading || !newShareEmail}
              className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center"
            >
              {actionLoading && <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1.5"></div>}
              Share
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && shares.length === 0 && (
        <p className="text-sm text-muted-foreground italic py-4 text-center">
          OneDrive is not shared with anyone
        </p>
      )}

      {/* Shares List */}
      {!loading && !error && shares.length > 0 && (
        <ul className="divide-y divide-border">
          {shares.map((share) => (
            <li key={share.id} className="py-3 flex items-center justify-between">
              <div className="flex items-center min-w-0">
                <div className="flex-shrink-0 h-8 w-8 bg-muted rounded-full flex items-center justify-center mr-3">
                  <span className="text-sm font-medium text-muted-foreground">
                    {share.userName?.charAt(0)?.toUpperCase() || share.userEmail?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {share.userName || share.userEmail}
                  </p>
                  {share.userName && share.userEmail !== share.userName && (
                    <p className="text-xs text-muted-foreground truncate">{share.userEmail}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 ml-4">
                {editingId === share.id ? (
                  <>
                    <select
                      value={editAccessLevel}
                      onChange={(e) => setEditAccessLevel(e.target.value as 'read' | 'write')}
                      className="text-xs px-2 py-1 border border-border rounded bg-card text-foreground"
                      disabled={actionLoading}
                    >
                      <option value="read">View</option>
                      <option value="write">Edit</option>
                    </select>
                    <button
                      onClick={() => handleUpdateAccess(share.id)}
                      disabled={actionLoading}
                      className="text-green-600 hover:text-green-800 disabled:opacity-50"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      disabled={actionLoading}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </>
                ) : (
                  <>
                    {getAccessLevelBadge(share.accessLevel)}
                    {share.accessLevel !== 'owner' && (
                      <button
                        onClick={() => {
                          setEditingId(share.id);
                          setEditAccessLevel(share.accessLevel === 'write' ? 'write' : 'read');
                        }}
                        disabled={actionLoading}
                        className="text-muted-foreground hover:text-foreground disabled:opacity-50"
                        title="Change access level"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleRevokeAccess(share)}
                      disabled={actionLoading}
                      className="text-red-500 hover:text-red-700 disabled:opacity-50"
                      title="Revoke access"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Note about sharing links */}
      {!loading && shares.some((s) => s.shareType === 'link') && (
        <p className="text-xs text-muted-foreground mt-2">
          Note: Some shares may be via sharing links rather than direct permissions.
        </p>
      )}
    </div>
  );
}
